import env from 'src/env.json';
import { FileAttachment } from '../AiAgent/ChatWebSocketClient';
import { Logger } from 'src/utils/logger';
import Http from 'src/api/Http/Http';
import { InterceptableAxiosRequestConfig } from 'src/api/Http/Http.types';
import { isNotFoundError } from 'src/api/Http/http-error-kind.logic';

// Create a shared Http instance for API calls with proper token refresh handling
const http = new Http();

// Define the base URL for API calls
const BE_API_URL = env.API_URL || 'http://localhost/api';
const PROXY_API_URL = env.API_PROXY_URL || 'http://localhost:5333';

/**
 * AiAgentApiService
 *
 * A shared service for interacting with the AI Agent API endpoints.
 * This service provides centralized methods for common operations like:
 * - Getting the agent ID
 * - Fetching media items
 *
 * It can be used by both the MediaService and RequestMediaService to prevent code duplication.
 */
export class AiAgentApiService {
  private agentId: string | null = null;
  private readonly _beApiUrl: string;
  private readonly _proxyApiUrl: string;
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static CACHE_TTL = 15000; // 15 seconds cache validity

  // Static method to clear the cache
  static clearCache(): void {
    AiAgentApiService.cache.clear();
  }

  constructor(beApiUrl: string = BE_API_URL, proxyApiUrl: string = PROXY_API_URL) {
    this._beApiUrl = beApiUrl;
    this._proxyApiUrl = proxyApiUrl;
  }

  /**
   * Get the base URL for API calls
   */
  get beApiUrl(): string {
    return this._beApiUrl;
  }

  get proxyApiUrl(): string {
    return this._proxyApiUrl.replace(/\/+$/, '');
  }

  /**
   * Helper method to get additional headers for API calls
   * Note: Authorization header is handled by the Http client automatically
   */
  get headers(): Record<string, string> {
    const headers: Record<string, string> = {};

    // Add x-brand-id header if a brand is selected
    const selectedBrandId = this.getSelectedBrandId();
    if (selectedBrandId) {
      headers['x-brand-id'] = selectedBrandId;
    }

    return headers;
  }

  /**
   * Get the selected brand ID from cookie (same as BrandSelectionContext uses)
   */
  private getSelectedBrandId(): string | null {
    const BRAND_COOKIE_NAME = 'selectedBrandId';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${BRAND_COOKIE_NAME}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Gets the agent ID, fetching it first if not already available
   *
   * @param suppressErrorToast - Forwarded to the lookup request when this resolves the agent
   *   id by calling the API. A caller that silences the toast for its OWN request has to
   *   silence this one too: it is awaited first, it is retried on every call while it keeps
   *   failing (only a SUCCESS is memoised), and a polling caller therefore stacks exactly the
   *   toasts it opted out of.
   * @returns Promise with the agent ID string
   */
  async getAgentId(suppressErrorToast = false): Promise<string> {
    if (this.agentId) {
      return this.agentId;
    }

    await this.initializeAgentId(suppressErrorToast);
    return this.agentId || '';
  }

  /**
   * Fetches the agent ID from the backend
   * This should be called before any API requests that require the agent_id
   */
  async initializeAgentId(suppressErrorToast = false): Promise<void> {
    // First, clear any problematic cached agent ID
    if (localStorage.getItem('be_creatives_agent_id') === 'default-agent-id-for-testing') {
      Logger.warn('Removing test agent ID from localStorage', 'AiAgentApiService');
      localStorage.removeItem('be_creatives_agent_id');
    }

    // Check for cached agent ID in localStorage
    const cachedAgentId = localStorage.getItem('be_creatives_agent_id');
    if (cachedAgentId) {
      this.agentId = cachedAgentId;
      return;
    }

    try {
      // Fix the base URL to ensure proper API path construction
      let baseUrl = this.beApiUrl;
      // Remove any trailing slashes
      if (baseUrl.endsWith('/')) {
        baseUrl = baseUrl.slice(0, -1);
      }

      // Ensure we have /api in the path
      let endpoint;
      if (baseUrl.endsWith('/api')) {
        // If URL ends with /api, use it directly
        endpoint = `${baseUrl}/ai-agent`;
      } else {
        // Otherwise ensure we add /api
        endpoint = `${baseUrl}/api/ai-agent`;
      }

      const response: any = await http.get(endpoint, {
        headers: this.headers,
        ...(suppressErrorToast ? { _noIntercept: true } : {}),
      } as InterceptableAxiosRequestConfig);

      // Handle various response formats
      // Note: Http client returns response.data directly
      let agentId: string | null = null;

      if (response && response.id) {
        // Standard format: { id: "agent-id" }
        agentId = response.id;
      } else if (response && response.agent && response.agent.id) {
        // Alternative format: { agent: { id: "agent-id" } }
        agentId = response.agent.id;
      } else if (response && response.data && response.data.id) {
        // Wrapper format: { data: { id: "agent-id" } }
        agentId = response.data.id;
      } else {
        Logger.error('Unexpected response format from agent API', 'AiAgentApiService', response);
        throw new Error('Could not extract agent ID from response');
      }

      if (agentId) {
        // Save to local state and localStorage
        this.agentId = agentId;
        localStorage.setItem('be_creatives_agent_id', agentId);
      } else {
        throw new Error('No agent ID returned from server');
      }
    } catch (error: any) {
      Logger.error('Error initializing agent ID', 'AiAgentApiService', error);
      // Rethrow the ORIGINAL rejection. Wrapping it in `new Error(...)` dropped `code`, so
      // callers classifying the failure (isNotFoundError, isAuthFailureError) saw an
      // unclassifiable error and had to treat a 404 or a dead session as a generic fault —
      // and the wrapper's internal text was what got rendered to the user.
      throw error;
    }
  }

  /**
   * Gets an upload URL for media files
   *
   * @param payload - Request payload containing file information and path
   * @returns Promise with the upload URL and token
   */
  async getUploadUrl(payload: {
    file_name: string;
    file_size: number;
    user_path: string[];
    directory_id?: string;
    brandId?: string; // Brand ID to pass as user_id parameter
    [key: string]: any;
  }): Promise<{ upload_url: string; upload_token: string }> {
    try {
      // Ensure we have the agent ID
      const agentId = await this.getAgentId();

      // Add agent ID to payload
      const requestPayload: any = {
        ...payload,
        agent_id: agentId, // Ensure agent_id is included
      };

      // Add brand ID as user_id if provided
      if (payload.brandId) {
        requestPayload.user_id = payload.brandId;
        Logger.debug('Adding user_id to payload:', 'AiAgentApiService', payload.brandId);
        // Remove brandId from payload since it's not a backend parameter
        delete requestPayload.brandId;
      }

      // Make the API call with a timeout to prevent hanging
      const responseData: any = await http.post(
        `${this.beApiUrl}/ai-agent/media/get-upload-url`,
        requestPayload,
        {
          headers: this.headers,
          timeout: 15000, // 15 second timeout to prevent indefinite hanging
        },
      );

      // Note: Http client returns response.data directly

      if (!responseData.upload_url || !responseData.upload_token) {
        throw new Error('Invalid response: missing upload_url or upload_token');
      }

      return {
        upload_url: responseData.upload_url,
        upload_token: responseData.upload_token,
      };
    } catch (error: any) {
      Logger.error('Error getting upload URL', 'AiAgentApiService', error);
      throw new Error(`Failed to get upload URL: ${error.message}`);
    }
  }

  /**
   * Gets media items from the AI Agent API
   *
   * @param params - Query parameters for the API request
   * @param params.path - Directory path to filter by
   * @param params.mime_type - MIME type to filter by
   * @param params.search - Search query to filter by
   * @param params.query - Alternative search query parameter
   * @param params.user_id - Brand ID to filter by (backend expects brand ID as user_id)
   * @param forceRefresh - If true, bypass cache and force a new API request
   * @param suppressErrorToast - Silence Http's GLOBAL destructive toast for this call only,
   *   for callers that render the failure themselves. Silences ONLY the toast: the 401
   *   refresh-and-retry runs earlier in the response interceptor and is unaffected. Same
   *   mechanism and reason as `transcription-api.service.ts` while it polls getJobStatus.
   *
   *   Note this endpoint answers a MISSING FOLDER with a 404 (agent-backend's `getMediaList`
   *   404s when the path row does not exist), which the interceptor does not treat specially
   *   — it only skips the toast for 401/403, and `setToastError` only early-returns for 422.
   *   A caller that polls a path which is created lazily therefore needs this, and needs to
   *   decide for itself what a 404 means (see MyInspirations).
   * @returns Promise with the media response
   */
  async getMediaItems(
    params: {
      path?: string;
      mime_type?: string;
      search?: string;
      query?: string;
      user_id?: string; // Brand ID for filtering media by brand
      [key: string]: any;
    },
    forceRefresh = false,
    suppressErrorToast = false,
  ): Promise<any> {
    try {
      // Ensure we have the agent ID. Suppression is forwarded: this request is awaited
      // before the media read, so an un-suppressed failure here is a toast per poll.
      const agentId = await this.getAgentId(suppressErrorToast);

      // Add agent ID to params
      const queryParams = {
        agent_id: agentId,
        ...params,
      };

      // Generate cache key based on the query parameters
      const cacheKey = `media:${JSON.stringify(queryParams)}`;

      // Check cache first (unless forceRefresh is true)
      const now = Date.now();
      const cachedData = AiAgentApiService.cache.get(cacheKey);

      if (!forceRefresh && cachedData && now - cachedData.timestamp < AiAgentApiService.CACHE_TTL) {
        return cachedData.data;
      }

      // Make the API request
      // Note: Http client returns response.data directly
      const response = await http.get(`${this.beApiUrl}/ai-agent/media`, {
        headers: this.headers,
        params: queryParams,
        ...(suppressErrorToast ? { _noIntercept: true } : {}),
      } as InterceptableAxiosRequestConfig);

      // Process the response data to ensure URL properties are set properly
      const processedData = this.processMediaResponse(response);

      // Cache the processed result
      AiAgentApiService.cache.set(cacheKey, {
        data: processedData,
        timestamp: now,
      });

      return processedData;
    } catch (error: any) {
      // Downgrade the EXPECTED absence only. A caller that suppresses the toast because it
      // handles 404 itself (the Inspirations folder does not exist until the save finishes)
      // should not also silence 401s, 500s and network faults — at the sandbox's
      // LOG_LEVEL=info a debug line is invisible, so that combination hid genuine failures
      // behind a caller's opt-out of an unrelated concern.
      if (suppressErrorToast && isNotFoundError(error)) {
        Logger.debug('Media items request failed (suppressed)', 'AiAgentApiService', error);
      } else {
        Logger.error('Error fetching media items', 'AiAgentApiService', error);
      }
      throw error;
    }
  }

  /**
   * Process media response to ensure all files have the necessary URL properties
   * for proper display in the media gallery components
   *
   * @param data - The raw response data from the API
   * @returns The processed response data with URL properties added
   */
  /**
   * Analyzes a media item using AI to generate tags and descriptions
   *
   * @param mediaId - ID of the media to analyze
   * @param force - Whether to force reanalysis even if already analyzed
   * @returns Promise with the analysis response
   */
  async analyzeMedia(mediaId: string, force = false): Promise<any> {
    try {
      // Construct the proper API endpoint
      const endpoint = `${this.beApiUrl}/ai-agent/media/${mediaId}/analyze`;

      // Prepare payload - only include force if it's true
      const payload = force ? { force: true } : {};

      const response = await http.post(endpoint, payload, {
        headers: this.headers,
      });

      // Note: Http client returns response.data directly
      return response;
    } catch (error) {
      Logger.error('Error analyzing media', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Checks the analysis status of a media item by fetching the media details
   * Uses GET instead of POST to avoid re-triggering analysis
   *
   * @param mediaId - ID of the media to check
   * @returns Promise with the current analysis state
   */
  async checkAnalysisStatus(mediaId: string): Promise<any> {
    try {
      // Use GET to fetch media details which includes analyze_state
      // This avoids re-triggering analysis like POST would
      const endpoint = `${this.beApiUrl}/ai-agent/media/${mediaId}`;

      const response = await http.get(endpoint, {
        headers: this.headers,
      });

      // Wrap response in data property for consistency with polling expectations
      return { data: response };
    } catch (error) {
      Logger.error('Error checking analysis status', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Polls the analysis status until completion or timeout
   * Uses exponential backoff for better responsiveness and reduced server load
   *
   * Valid analyze_state values:
   * - 'PENDING' - Analysis queued but not started
   * - 'IN_PROGRESS' - Analysis currently running
   * - 'COMPLETED' - Analysis finished successfully
   * - 'FAILED' - Analysis encountered an error
   *
   * @param mediaId - ID of the media to poll
   * @param options - Polling configuration options
   * @returns Promise with the final analysis result (or latest status if timeout)
   */
  async pollAnalysisStatus(
    mediaId: string,
    options: {
      maxAttempts?: number;
      initialInterval?: number;
      maxInterval?: number;
      maxConsecutiveErrors?: number;
    } = {},
  ): Promise<any> {
    const {
      maxAttempts = 60,
      initialInterval = 500,
      maxInterval = 3000,
      maxConsecutiveErrors = 5,
    } = options;

    let attempts = 0;
    let errorCount = 0;
    let currentInterval = initialInterval;
    const backoffMultiplier = 1.5;

    const checkStatus = async (): Promise<any> => {
      try {
        const response = await this.checkAnalysisStatus(mediaId);

        // Reset error count on successful request
        errorCount = 0;

        // Handle the case when media is already analyzed
        // Note: This message may come from POST /analyze endpoint, not GET /media/{id}
        if (response?.data?.analyze_state === 'COMPLETED') {
          return response;
        }

        if (!response?.data) {
          throw new Error('Invalid analysis response format');
        }

        // Check if analysis reached a terminal state (COMPLETED or FAILED)
        // Intermediate states (PENDING, IN_PROGRESS) will continue polling
        if (
          response.data.analyze_state === 'COMPLETED' ||
          response.data.analyze_state === 'FAILED'
        ) {
          return response;
        }

        // Check if we've reached the maximum number of attempts
        if (++attempts >= maxAttempts) {
          Logger.warn(
            `Media analysis polling timed out after ${maxAttempts} attempts`,
            'AiAgentApiService',
            { mediaId },
          );
          return response;
        }

        // Wait with exponential backoff
        await new Promise((resolve) => setTimeout(resolve, currentInterval));
        currentInterval = Math.min(currentInterval * backoffMultiplier, maxInterval);

        return checkStatus();
      } catch (error) {
        Logger.error('Error during analysis polling', 'AiAgentApiService', error);
        errorCount++;

        // Prevent infinite error loops
        if (errorCount >= maxConsecutiveErrors) {
          throw error;
        }

        // Retry on transient errors with longer delay
        await new Promise((resolve) => setTimeout(resolve, currentInterval * 2));
        currentInterval = Math.min(currentInterval * backoffMultiplier, maxInterval);
        return checkStatus();
      }
    };

    return checkStatus();
  }

  private processMediaResponse(data: any): any {
    // Create a deep copy to avoid modifying the original response
    const processedData = { ...data };

    // Define an interface for the media file structure
    interface MediaFile {
      id: string;
      file_name?: string;
      mime_type?: string;
      url?: string;
      index_url?: string;
      custom_properties?: {
        originalUrl?: string;
        [key: string]: any;
      };
      video?: {
        thumbnail_url?: string;
        [key: string]: any;
      };
      generated_conversions?: {
        thumbnail?: string;
        [key: string]: any;
      };
      [key: string]: any; // Allow for additional properties
    }

    // Process files array if it exists
    if (processedData.files && Array.isArray(processedData.files)) {
      processedData.files = processedData.files.map((file: MediaFile) => {
        // Create a copy of the file to avoid modifying the original
        const processedFile = { ...file };

        // Ensure URL properties are set
        if (!processedFile.url && processedFile.index_url) {
          processedFile.url = processedFile.index_url;
        }

        // Add URL to custom_properties if it doesn't exist
        if (
          processedFile.url &&
          (!processedFile.custom_properties || !processedFile.custom_properties.originalUrl)
        ) {
          processedFile.custom_properties = processedFile.custom_properties || {};
          processedFile.custom_properties.originalUrl = processedFile.url;
        }

        // Handle thumbnails for video types
        if (processedFile.mime_type && processedFile.mime_type.startsWith('video/')) {
          // Create video property if it doesn't exist
          processedFile.video = processedFile.video || {};

          // Set thumbnail URL if not already set
          if (!processedFile.video.thumbnail_url && processedFile.url) {
            processedFile.video.thumbnail_url = processedFile.url;
          }
        }

        // Ensure we have generated_conversions for thumbnail handling
        if (
          processedFile.url &&
          (!processedFile.generated_conversions || !processedFile.generated_conversions.thumbnail)
        ) {
          processedFile.generated_conversions = processedFile.generated_conversions || {};
          processedFile.generated_conversions.thumbnail = processedFile.url;
        }

        return processedFile;
      });
    }

    // Handle case where data is nested in 'data' property
    if (processedData.data && processedData.data.files && Array.isArray(processedData.data.files)) {
      processedData.data.files = processedData.data.files.map((file: MediaFile) => {
        // Process nested files with the same logic
        const processedFile = { ...file };

        if (!processedFile.url && processedFile.index_url) {
          processedFile.url = processedFile.index_url;
        }

        if (
          processedFile.url &&
          (!processedFile.custom_properties || !processedFile.custom_properties.originalUrl)
        ) {
          processedFile.custom_properties = processedFile.custom_properties || {};
          processedFile.custom_properties.originalUrl = processedFile.url;
        }

        if (processedFile.mime_type && processedFile.mime_type.startsWith('video/')) {
          processedFile.video = processedFile.video || {};
          if (!processedFile.video.thumbnail_url && processedFile.url) {
            processedFile.video.thumbnail_url = processedFile.url;
          }
        }

        if (
          processedFile.url &&
          (!processedFile.generated_conversions || !processedFile.generated_conversions.thumbnail)
        ) {
          processedFile.generated_conversions = processedFile.generated_conversions || {};
          processedFile.generated_conversions.thumbnail = processedFile.url;
        }

        return processedFile;
      });
    }

    return processedData;
  }

  public async getChatSessions(params: {
    brand_id: string;
    flags?: string[]; // Filter by specific flags (e.g., ['favorite'])
    excludeFlags?: string[]; // Exclude sessions with these flags (e.g., ['hidden'])
  }): Promise<any[]> {
    try {
      // Construct the proper API endpoint
      const endpoint = `${this.beApiUrl}/ai-agent/chat/${params.brand_id}`;

      // Build query parameters
      const queryParams = new URLSearchParams();

      if (params.flags && params.flags.length > 0) {
        queryParams.append('flags', params.flags.join(','));
      }

      if (params.excludeFlags && params.excludeFlags.length > 0) {
        queryParams.append('excludeFlags', params.excludeFlags.join(','));
      }

      const url = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;

      // Note: Http client returns response.data directly
      const response = await http.get(url, {
        headers: this.headers,
      });

      const items = Array.isArray(response) ? response : [];
      return items as any[];
    } catch (error) {
      Logger.error('Error getting chat sessions', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Get messages for a specific chat session (lazy loading)
   * Calls: GET /api/ai-agent/chat/{brand_id}/{session_id}/messages
   */
  public async getSessionMessages(params: { brand_id: string; session_id: string }): Promise<any> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/chat/${params.brand_id}/${params.session_id}/messages`;

      Logger.info('Fetching session messages', 'AiAgentApiService', {
        brand_id: params.brand_id,
        session_id: params.session_id,
      });

      // Note: Http client returns response.data directly
      const response = await http.get(endpoint, {
        headers: this.headers,
      });

      // Response format: { sessionId, messages, lastAccessed, ttl }
      return response;
    } catch (error) {
      Logger.error('Failed to fetch session messages', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async deleteChatSession(params: {
    brand_id: string;
    chat_session_id: string;
  }): Promise<void> {
    try {
      // Construct the proper API endpoint
      const endpoint = `${this.beApiUrl}/ai-agent/chat/${params.brand_id}/${params.chat_session_id}`;

      await http.delete(endpoint, {
        headers: this.headers,
      });
    } catch (error) {
      Logger.error('Error deleting chat session', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Update chat history metadata
   * Can update: title, summary, duration_minutes, message_count, topics, flags
   * Calls: PATCH /api/ai-agent/chat/{chatHistoryId}
   */
  public async updateChatHistory(params: {
    chat_history_id: string;
    data: {
      title?: string;
      summary?: string;
      duration_minutes?: number;
      message_count?: number;
      topics?: string[];
      flags?: string[];
    };
  }): Promise<{ success: boolean }> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/chat/${params.chat_history_id}`;

      // Note: Http client returns response.data directly
      const response: any = await http.patch(endpoint, params.data, {
        headers: this.headers,
      });

      return response;
    } catch (error) {
      Logger.error('Error updating chat history', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async uploadChatFile(payload: {
    brand_id: string;
    chat_session_id: string;
    file: File;
  }): Promise<FileAttachment> {
    try {
      const agentId = await this.getAgentId();
      // Construct the proper API endpoint
      const endpoint = `${this.proxyApiUrl}/api/chat/upload`;
      const formData = new FormData();
      formData.append('file', payload.file);
      formData.append('agentId', agentId);
      formData.append('userId', payload.brand_id);
      formData.append('sessionId', payload.chat_session_id);

      // Note: Http client returns response.data directly
      const response: any = await http.post(
        endpoint,
        {
          agentId: agentId,
          userId: payload.brand_id,
          sessionId: payload.chat_session_id,
          file: payload.file,
        },
        {
          headers: {
            ...this.headers,
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      const file = response?.file;
      if (!file) {
        throw new Error('Failed to upload chat file');
      }

      return {
        bucket: file.bucket,
        id: file.id,
        fileName: file.fileName,
        mimeType: file.mimeType,
        size: file.size,
        url: file.url,
      };
    } catch (error) {
      Logger.error('Error uploading chat file', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async getMappedMemories(props: {
    brand_id: string;
    startDate: Date;
    endDate: Date;
  }): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      params.append('userId', props.brand_id);
      params.append('startDate', props.startDate.toISOString());
      params.append('endDate', props.endDate.toISOString());
      params.append('labels', 'content-plan');
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory?${params.toString()}`;

      // Note: Http client returns response.data directly
      const response = await http.get(endpoint, {
        headers: this.headers,
      });

      const items = Array.isArray(response) ? response : [];
      return items as any[];
    } catch (error) {
      Logger.error('Error getting mapped memories', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async getStatusBrandProfileCreation(props: { brand_id: string }): Promise<{
    status: 'need_restart' | 'success' | 'pending' | 'not_run';
  }> {
    try {
      const endpoint = `${this.beApiUrl}/brands/status-brand-profile-creation/${props.brand_id}`;
      // Note: Http client returns response.data directly
      const response: any = await http.get(endpoint, {
        headers: this.headers,
      });

      return {
        status: response?.status,
      };
    } catch (error) {
      Logger.error('Error getting brand profile creation status:', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async updateContentPlanUsed(contentPlanId: string): Promise<void> {
    try {
      await this.putMappedMemory(contentPlanId, {
        labels: ['content-plan'],
        attributes: { used: true },
      });
    } catch (error) {
      Logger.error('Error updating content plan used status', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Updates the release date of a content plan
   *
   * @param contentPlanId - ID of the content plan to update
   * @param releaseDate - New release date for the content plan
   * @returns Promise<void>
   */
  public async updateContentPlanReleaseDate(
    contentPlanId: string,
    releaseDate: Date,
  ): Promise<void> {
    try {
      await this.putMappedMemory(contentPlanId, {
        labels: ['content-plan'],
        attributes: { 'release-date': releaseDate.toISOString() },
      });

      Logger.info(
        `Successfully updated content plan release date: ${contentPlanId} to ${releaseDate.toISOString()}`,
        'AiAgentApiService',
      );
    } catch (error) {
      Logger.error('Error updating content plan release date', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * GET a single mapped-memory row by id.
   *
   * Returns null ONLY for a genuine 404 (the row doesn't exist) — callers can treat
   * that as "nothing to link". Any OTHER failure (network blip, 401, 5xx, request
   * cancellation) is TRANSIENT and is re-thrown, so a caller can tell a missing row
   * apart from a recoverable error and not surface a permanent failure for something
   * a retry would fix. Deliberately does NOT log here: the caller logs once on the
   * surfaced failure, so a transient error isn't recorded twice.
   */
  public async getMappedMemoryById(id: string): Promise<any | null> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory/${id}`;
      // Http client returns response.data directly
      const response: any = await http.get(endpoint, { headers: this.headers });
      return response ?? null;
    } catch (error: any) {
      // HttpError.code === HTTP status (or an axios code / 0 for network errors),
      // so only a real 404 means "row doesn't exist"; everything else is transient.
      if (error?.code === 404) return null;
      throw error;
    }
  }

  /**
   * Low-level PUT to a mapped-memory row — the single primitive the higher-level
   * update helpers below share (`updateContentPlanUsed`, `updateContentPlanReleaseDate`,
   * `updateMappedMemoryTitle`, `linkScriptToThought`), so the endpoint/headers
   * boilerplate lives in one place.
   *
   * Backend semantics (see backend UpdateMappedMemoryDto::mergeWithOriginalMemory):
   * `attributes` are deep-merged key-by-key and `title`/`content` are back-filled
   * from the existing row when omitted — but `labels` are REPLACED. Callers that
   * must keep a row's labels should read and pass the row's real labels.
   */
  private async putMappedMemory(
    id: string,
    body: { labels?: string[]; attributes?: Record<string, any>; title?: string },
  ): Promise<void> {
    const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory/${id}`;
    await http.put(endpoint, body, { headers: this.headers });
  }

  /**
   * Links a generated script to an EXISTING recorded-thought/idea row, in place.
   *
   * Fetches the row first so we can:
   *  - APPEND to script_id (an Idea can link to multiple scripts — the detail
   *    view parses script_id as a comma-delimited list), de-duped, instead of
   *    replacing it (which would drop a previously-linked script). The PUT merges
   *    at the attribute-key level, so a bare { script_id } would overwrite the
   *    whole string.
   *  - Preserve the row's real `labels` (the PUT REPLACES labels, so hardcoding
   *    ['my_thought'] would narrow a row that carried extra labels).
   * The backend merges attributes, so seed / user_thought are preserved.
   *
   * PUT /api/ai-agent/mapped-memory/{id}
   *
   * @param thoughtId - ID of the existing my_thought / idea row
   * @param scriptId - ID of the saved script to link
   */
  public async linkScriptToThought(thoughtId: string, scriptId: string): Promise<void> {
    // Read-modify-write of the row's comma-delimited script_id list.
    // KNOWN LIMITATION: this client-side read-then-PUT is not atomic — two saves
    // against the SAME idea in quick succession (regenerate-then-save-both, two
    // tabs) can both read the same starting list and the last PUT wins, dropping
    // one script link. De-duping below prevents duplicates but not a lost-update.
    // The proper fix is a server-side append; tracked as a follow-up rather than
    // held here, since it only bites concurrent same-idea saves.
    const existing = await this.getMappedMemoryById(thoughtId);

    // getMappedMemoryById returns null ONLY for a real 404 (row genuinely gone) and
    // re-throws transient errors, so a null here means the idea row no longer exists.
    // Abort rather than proceed from an empty fallback — a bare { script_id:<newId> }
    // PUT would CLOBBER the existing comma-delimited list (the data loss this guards).
    // Transient read failures propagate straight out of getMappedMemoryById above.
    // Either way the caller logs once and shows the "script saved, couldn't auto-link"
    // toast.
    if (!existing) {
      throw new Error(
        `linkScriptToThought: thought ${thoughtId} not found (404) — aborting to avoid overwriting its script_id list`,
      );
    }

    // Append to the existing comma-delimited script_id list, de-duped.
    const existingIds = String(existing.attributes?.script_id || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!existingIds.includes(scriptId)) existingIds.push(scriptId);

    // Preserve the row's real labels (PUT replaces, doesn't merge, labels).
    const labels =
      Array.isArray(existing.labels) && existing.labels.length > 0
        ? existing.labels
        : ['my_thought'];

    await this.putMappedMemory(thoughtId, {
      labels,
      attributes: { script_id: existingIds.join(',') },
    });

    Logger.info(
      `Successfully linked script ${scriptId} to thought ${thoughtId} (now: ${existingIds.join(',')})`,
      'AiAgentApiService',
    );
  }

  public async requestStatusBrandProfileCreation(props: { brand_id: string }): Promise<boolean> {
    try {
      const endpoint = `${this.beApiUrl}/brands/request-brand-profile-creation/${props.brand_id}`;
      await http.post(
        endpoint,
        {},
        {
          headers: this.headers,
        },
      );

      return true;
    } catch (error) {
      Logger.error('Error getting brand profile creation status:', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Deletes a mapped memory from the AI Agent
   *
   * @param contentPlanId - ID of the content plan/memory to delete
   * @returns Promise<void>
   */
  public async deleteMappedMemory(contentPlanId: string): Promise<void> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory/${contentPlanId}`;

      await http.delete(endpoint, {
        headers: this.headers,
      });

      Logger.info(`Successfully deleted content plan: ${contentPlanId}`, 'AiAgentApiService');
    } catch (error) {
      Logger.error('Error deleting mapped memory', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Adds a new mapped memory to the AI Agent
   *
   * @param props - Object containing brand_id and memory data
   * @param props.brand_id - ID of the brand to associate with the memory
   * @param props.title - Title of the memory
   * @param props.content - Content of the memory
   * @param props.labels - Array of labels to categorize the memory
   * @param props.attributes - Object containing additional attributes for the memory
   * @returns Promise with the created memory data
   */
  public async addMappedMemory(props: {
    title: string;
    content: string;
    labels: string[];
    attributes: Record<string, any>;
  }): Promise<{ id: string }> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory`;

      // Note: Http client returns response.data directly
      const response: any = await http.post(
        endpoint,
        {
          title: props.title,
          content: props.content,
          labels: props.labels,
          attributes: props.attributes,
        },
        {
          headers: this.headers,
        },
      );

      return response as { id: string };
    } catch (error) {
      Logger.error('Error adding mapped memory', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Saves a recorded thought via the dedicated endpoint
   *
   * POST /api/ai-agent/mapped-memory/recorded-thought
   *
   * @param props - Object containing user_thought, seed, and script_id
   * @returns Promise with the created thought data including id
   */
  public async saveRecordedThought(props: {
    user_thought: string;
    seed: string;
    script_id: string | null;
    // session_id lets the backend dedup against the auto-saved raw thought (promote it
    // instead of creating a duplicate). Optional: the Save-thought and
    // Create-Script-from-thought paths both pass it; only the Turn-into-Idea
    // replacement flow (processThoughtReplacement) omits it — that path does its own
    // guarded delete-based dedup, and without session_id the backend can never return a
    // promoted (same) id there. The backend safely skips dedup when it's absent.
    session_id?: string;
  }): Promise<{ id: string; [key: string]: any }> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory/recorded-thought`;

      const response: any = await http.post(endpoint, props, {
        headers: this.headers,
      });

      return response;
    } catch (error) {
      Logger.error('Error saving recorded thought', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Updates the title of a mapped memory entry
   *
   * PUT /api/ai-agent/mapped-memory/{id}
   *
   * @param memoryId - ID of the mapped memory to update
   * @param title - New title for the memory
   * @returns Promise<void>
   */
  public async updateMappedMemoryTitle(memoryId: string, title: string): Promise<void> {
    try {
      await this.putMappedMemory(memoryId, { title });
    } catch (error) {
      Logger.error('Error updating mapped memory title', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Fetches recorded thoughts from mapped-memory API with label 'my_thought'
   *
   * GET /api/ai-agent/mapped-memory?userId={brandId}&labels=my_thought
   *
   * @param props - Object containing brand_id
   * @returns Promise with the mapped memory entries for thoughts
   */
  public async getMyThoughts(props: {
    brand_id: string;
  }): Promise<{ entries: any[]; total: number }> {
    try {
      const params = new URLSearchParams();
      params.append('userId', props.brand_id);
      params.append('labels', 'my_thought');
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory?${params.toString()}`;

      const response: any = await http.get(endpoint, {
        headers: this.headers,
      });

      return {
        entries: Array.isArray(response?.entries)
          ? response.entries
          : Array.isArray(response)
            ? response
            : [],
        total: Number(response?.total) || 0,
      };
    } catch (error) {
      Logger.error('Error getting my thoughts from mapped memory', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Request content plan update/extension
   * Uses the existing backend endpoint to generate 4 weeks of content ideas
   *
   * POST /api/brands/update-content-plan/{brandId}
   *
   * @param brandId - Brand ID to generate content plan for
   * @param startDate - Optional start date for content plan (defaults to next Monday on backend)
   * @returns Promise with success response
   */
  public async updateContentPlan(
    brandId: string,
    startDate?: string,
  ): Promise<{ message: string }> {
    try {
      const endpoint = `${this.beApiUrl}/brands/update-content-plan/${brandId}`;

      // Build request body - only include start_date if provided
      const requestBody = startDate ? { start_date: startDate } : {};

      Logger.info('Requesting content plan update', 'AiAgentApiService', {
        brandId,
        startDate,
      });

      const response: any = await http.post(endpoint, requestBody, {
        headers: this.headers,
      });

      Logger.info('Content plan update requested successfully', 'AiAgentApiService', {
        brandId,
        response,
      });

      return response;
    } catch (error) {
      Logger.error('Error requesting content plan update', 'AiAgentApiService', error);
      throw error;
    }
  }
}

// Export a singleton instance for easy importing
export const aiAgentApiService = new AiAgentApiService();
export default aiAgentApiService;
